<template>
    <v-dialog v-model="dialog" max-width="480" role="dialog" aria-modal="true" :aria-labelledby="titleId" :aria-describedby="descId" @keydown.esc="closeDialog">
        <v-card v-focus-trap="dialog">
            <v-card-title :id="titleId">
                <p class="sr-only" :id="descId">Choose responsible users and/or groups. Use Tab to navigate, Enter to confirm, or Escape to close.</p>
                {{ authors ? "Select responsible authors" : "Select responsible" }}
                <v-btn right absolute icon color="primary" @click="closeDialog" :aria-label="'Close dialog'">
                    <v-icon aria-hidden="true">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <!-- Tabs -->
                <v-tabs v-model="selectedTab" color="primary" background-color="transparent" :aria-label="'Selection type tabs'">
                    <v-tab v-for="(tab, i) in tabs" :key="tab" :disabled="authors && i === 1">
                        {{ tab }}
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="selectedTab">
                    <!-- User Tab -->
                    <v-tab-item class="mt-5">
                        <v-autocomplete
                            data-focus-first
                            ref="userAuto"
                            v-model="selectedUser"
                            :items="userOptions"
                            item-text="username"
                            item-value="id"
                            label="Search user"
                            multiple
                            return-object
                            hide-details
                            rounded
                            filled
                            :menu-props="{ contentClass: 'rs-autocomplete-menu' }"
                            :aria-label="'Search and select users'">
                            <!-- Selected chips -->
                            <template v-slot:selection="{ item, index }">
                                <v-chip class="pl-2 pr-1" close @click:close="removeSelectedUser(index)">
                                    <v-avatar
                                        size="24"
                                        class="white--text mr-2"
                                        style="font-size: 10px"
                                        :style="{ backgroundColor: $helpers.stringToBackgroundColor(item.username) }"
                                        :aria-hidden="true">
                                        {{ $helpers.getInitials(item.username) }}
                                    </v-avatar>
                                    <span>{{ item.username }}</span>
                                </v-chip>
                            </template>

                            <!-- Dropdown items -->
                            <template v-slot:item="{ item }">
                                <v-list-item two-line>
                                    <v-list-item-icon class="mr-5 pt-4">
                                        <v-avatar
                                            size="32"
                                            class="white--text"
                                            style="font-size: 12px"
                                            :style="{ backgroundColor: $helpers.stringToBackgroundColor(item.username) }"
                                            :aria-hidden="true">
                                            {{ $helpers.getInitials(item.username) }}
                                        </v-avatar>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.username }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                            <v-chip outlined color="primary" small class="pl-2">
                                                <v-icon small class="mr-1" color="grey">mdi-email</v-icon>
                                                {{ item.user }}
                                            </v-chip>
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider />
                            </template>
                        </v-autocomplete>
                    </v-tab-item>

                    <!-- Group Tab -->
                    <v-tab-item class="mt-5">
                        <v-autocomplete v-model="selectedGroup" :items="groupOptions" label="Search group" multiple hide-details rounded filled dense :aria-label="'Search and select groups'" />
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn rounded color="primary" class="mx-auto" @click="assignResponsible" :aria-label="'Confirm selection'"> OK </v-btn>
                <v-spacer />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    export default {
        name: "ResponsibleSelection",
        props: {
            authors: { type: Boolean, default: false },
            value: { type: Boolean, default: false }, // v-model from parent
            user: { type: Array, default: () => [] } // preselected users from parent (optional)
        },
        data() {
            return {
                dialog: false,
                tabs: ["User", "Group"],
                selectedTab: 0, // index-based to keep <v-tabs> and <v-tabs-items> in sync
                selectedUser: [], // arrays (not null) to avoid v-model glitches
                selectedGroup: [],
                userOptions: [], // will be filled from API
                groupOptions: [] // placeholder; wire up when groups API is ready
            }
        },
        computed: {
            titleId() {
                return "responsible-selection-title"
            },
            descId() {
                return "responsible-selection-desc"
            }
        },
        watch: {
            // sync external v-model
            value(newVal) {
                this.dialog = newVal
                if (newVal) {
                    // ensure state is consistent when opened
                    this.ensureArrays()
                }
            },
            // reflect internal dialog back to parent v-model
            dialog(newVal) {
                this.$emit("input", newVal)
            },
            // keep selected users in sync when parent changes
            user: {
                immediate: true,
                handler(val) {
                    if (Array.isArray(val)) {
                        // Map by id if coming from parent
                        this.selectedUser = val.map((u) => this.findUserById(u.id) || u).filter(Boolean)
                    } else {
                        this.selectedUser = []
                    }
                }
            }
        },
        methods: {
            ensureArrays() {
                if (!Array.isArray(this.selectedUser)) this.selectedUser = []
                if (!Array.isArray(this.selectedGroup)) this.selectedGroup = []
            },
            removeSelectedUser(index) {
                if (index > -1) this.selectedUser.splice(index, 1)
            },
            async fetchUser() {
                try {
                    const post_data = { company_id: localStorage.company_id }
                    const url = process.env.VUE_APP_APIURL + "/user/companyuser"
                    const response = await this.$helpers.axiosPost(url, post_data)
                    // Keep only content-suite users
                    this.userOptions = (response?.data || []).filter((u) => u.content_suite == 1)
                    // Reconcile selectedUser with fresh options (avoid ghosts)
                    this.selectedUser = (this.selectedUser || []).map((u) => this.findUserById(u.id) || u).filter(Boolean)
                } catch (e) {
                    console.log("fetchUser error", e)
                    this.userOptions = []
                }
            },
            // TODO: implement real API when available
            async fetchGroups() {
                this.groupOptions = [] // keep empty to avoid fake data in production
            },
            findUserById(id) {
                return (this.userOptions || []).find((u) => u.id === id)
            },
            closeDialog() {
                this.dialog = false
                this.selectedUser = Array.isArray(this.selectedUser) ? this.selectedUser : []
                this.selectedGroup = Array.isArray(this.selectedGroup) ? this.selectedGroup : []
                this.$emit("closed")
            },

            assignResponsible() {
                this.$emit("assign", { user: this.selectedUser, groups: this.selectedGroup })
                this.dialog = false
                this.$emit("closed")
            }
        },
        async mounted() {
            await this.fetchUser()
            await this.fetchGroups()
            this.dialog = this.value
            this.$nextTick(() => {
                const el = this.$el && this.$el.querySelector(".v-autocomplete input")
                if (el) el.focus()
            })
        }
    }
</script>
<style scoped>
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
</style>
